import { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import classNames from 'classnames';
import {
  Session,
  StudyType,
  useAssignPlanToMe,
  useAssignSessionToMe,
  useCleanSession,
  useClient,
  useCurrentOrganizationId,
  useIncentiveText,
  useOrganization,
  useOrganizationSubscription,
  usePlan,
  usePlanCapabilities,
  useToggleAutomaticRejectDisabled,
} from '@app/api';
import { SessionBoxInfo } from 'components/Session/SessionBoxInfo';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  CenteredLoader,
  CenteredModal,
  Chip,
  DurationChip,
  useParticipantDisplayName,
  IconChip,
  NotFound,
  SessionFormatChip,
  Tooltip,
  useBoolModalState,
} from '@app/core';
import { config } from 'config';
import { Toggle } from '@app/form';
import { useOrganizationMembers } from 'queries';
import { toast } from 'react-toastify';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  sessionBoxesLine: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'center',
    padding: [theme.marginBase * 2, 0],
  },
  box: {
    flex: 1,
    width: '100%',
    marginBottom: theme.marginBase * 2,
  },
  formatBoxContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& > i': {
      fontSize: theme.iconSize.large,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'left',
    padding: [theme.marginBase, 0],
    marginBottom: theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h5,
    textTransform: 'uppercase',
    color: theme.colors.inkSecondary,
  },
  insideBoxText: {
    ...theme.fonts.label,
    fontWeight: 400,
  },
  tagInfosContainer: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
    marginBottom: theme.marginBase * 2,
  },
  tagsContainer: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
  },
  oneTagByLine: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  blueIcons: {
    color: theme.colors.blueAccessible,
  },
  targetText: {
    ...theme.fonts.label,
  },
  link: {
    ...theme.fonts.caption,
    textDecoration: 'underline',
    color: theme.colors.blueAccessible,
    flexBasis: '100%',
  },
  name: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase,
  },
  toggleContainer: {
    margin: 0,
    marginTop: theme.marginBase * 2,
  },
  assignButton: {
    background: theme.colors.purpleAccessible,
    color: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    paddingLeft: theme.marginBase,
    paddingRight: theme.marginBase,
  },
  modalInnerGap: {
    display: 'flex',
    gap: theme.marginBase,
  },
}));

interface Props {
  session: Session;
}

export const MonitoringAdminSidePanel = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [warnOpened, openWarning, closeWarning] = useBoolModalState();
  const { data: owner, isLoading: ownerLoading } = useClient(session.ownerId);
  const { data: capabilities } = usePlanCapabilities(session.planId);
  const { data: organization } = useOrganization(session.organizationId);
  const { data: subscriptions } = useOrganizationSubscription(
    session.organizationId,
  );
  const { data: plan } = usePlan(session.planId);
  const incentiveText = useIncentiveText(session.id, session, session.locale);
  const { mutate: assignSessionToMe } = useAssignSessionToMe(session.id);
  const { mutate: assignPlanToMe } = useAssignPlanToMe(session.planId);
  const { mutate: cleanSession } = useCleanSession(session.id);

  const tandemzId = useCurrentOrganizationId();
  const { data: members } = useOrganizationMembers(tandemzId);

  const sales = useMemo(
    () =>
      plan?.assignedToId
        ? members?.find((m) => m.accountId === plan?.assignedToId)?.account
        : undefined,
    [members, plan],
  );
  const salesName = useParticipantDisplayName(sales);

  const pm = useMemo(
    () =>
      session.assignedToId
        ? members?.find((m) => m.accountId === session.assignedToId)?.account
        : undefined,
    [members, session],
  );
  const pmName = useParticipantDisplayName(pm);

  const { mutate: toggleAutoRejectDisabled } = useToggleAutomaticRejectDisabled(
    session.id,
  );
  const onSessionClean = () =>
    cleanSession(undefined, {
      onSuccess: () => {
        toast.success('Session anonymisée');
      },
      onError: () => {
        toast.error('Anonymisation échouée, veuillez réessayer.');
      },
      onSettled: () => {
        closeWarning();
      },
    });

  if (ownerLoading) {
    return <CenteredLoader />;
  }

  if (!session) {
    return <NotFound />;
  }

  return (
    <div className={classes.sessionBoxesLine}>
      <div className={classes.titleContainer}>
        <h1 className={classes.title}>
          {t('sessions.details.monitoringAdmin.title')}
        </h1>
      </div>
      <div className={classes.tagInfosContainer}>
        <SessionFormatChip session={session} color="blue" />
        <DurationChip session={session} color="blue" />
        {capabilities?.automaticInvitationLaunch && (
          <IconChip
            icon="ri-settings-3-line"
            color="inkSecondary"
            label={t('sessions.details.monitoringAdmin.studyProducts.auto')}
          />
        )}
        {capabilities?.accountManager && (
          <IconChip
            icon="ri-settings-3-line"
            color="inkSecondary"
            label={t('sessions.details.monitoringAdmin.studyProducts.manager')}
          />
        )}
        {capabilities?.turnkey && (
          <IconChip
            icon="ri-settings-3-line"
            color="inkSecondary"
            label={t('sessions.details.monitoringAdmin.studyProducts.turnkey')}
          />
        )}
        {capabilities?.verifySetup && (
          <IconChip
            icon="ri-settings-3-line"
            color="inkSecondary"
            label={t(
              'sessions.details.monitoringAdmin.studyProducts.verifySetup',
            )}
          />
        )}
        {capabilities?.askDocument && (
          <IconChip
            icon="ri-settings-3-line"
            color="inkSecondary"
            label={t(
              'sessions.details.monitoringAdmin.studyProducts.askDocument',
            )}
          />
        )}
        <IconChip
          icon="ri-briefcase-3-line"
          color="inkSecondary"
          label={session.studySubject}
        />
        {session.studySubject && (
          <IconChip icon="ri-gift-line" color="green" label={incentiveText} />
        )}
        <a
          className={classes.link}
          href={`${config.FOREST_URL_PREFIX}/session/index/record/session/${session.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Trans i18nKey="sessions.details.monitoring.forestLink" />
        </a>
        {session.studyType === StudyType.UNMODERATED && (
          <Toggle
            containerClass={classes.toggleContainer}
            label={t('sessions.details.monitoring.removeAutoReject')}
            checked={session.autoRejectDisabled}
            onChange={() => toggleAutoRejectDisabled()}
          />
        )}
      </div>
      <SessionBoxInfo
        color="blue"
        title={t('sessions.details.monitoringAdmin.owner')}
        className={classes.box}
        icon="ri-user-3-line"
      >
        <h5 className={classes.name}>
          {owner?.account
            ? [owner.account.firstName, owner.account.lastName]
                .filter((p) => !!p)
                .join(' ')
            : '????????'}
        </h5>
        <div className={classes.tagsContainer}>
          <IconChip icon="ri-mail-line" label={owner?.account?.email} />
          <IconChip icon="ri-phone-line" label={owner?.phoneNumber} />
        </div>
      </SessionBoxInfo>
      <SessionBoxInfo
        color="red"
        title={t('sessions.details.monitoringAdmin.organization')}
        className={classes.box}
        icon="ri-building-line"
      >
        <h5 className={classes.name}>{organization?.name}</h5>
        <Tooltip
          requireDiv
          title={Object.keys(capabilities || {})
            .map((key) => {
              return `${key}: ${
                capabilities?.[key as keyof typeof capabilities] ? 'oui' : 'non'
              }`;
            })
            .join('\n')}
        >
          <div>
            {subscriptions?.length ? (
              subscriptions.map((s) => (
                <div className={classes.tagsContainer}>
                  <Chip color="red">{s.offer?.name}</Chip>
                  {!!s.products?.length && (
                    <Chip color="red">
                      {t(
                        'sessions.details.monitoringAdmin.subscriptions.extraProducts',
                        {
                          count: s.products.length,
                        },
                      )}
                    </Chip>
                  )}
                </div>
              ))
            ) : (
              <div className={classes.tagsContainer}>
                <Chip color="red">
                  {t('sessions.details.monitoringAdmin.subscriptions.none')}
                </Chip>
              </div>
            )}
          </div>
        </Tooltip>
      </SessionBoxInfo>
      <SessionBoxInfo
        color="purple"
        title="Personnes assignées"
        className={classes.box}
        icon="ri-group-2-line"
      >
        <div
          className={classNames(classes.tagsContainer, classes.oneTagByLine)}
        >
          <IconChip icon="ri-money-euro-box-line" color="purple">
            Sales :{' '}
            {sales ? (
              salesName
            ) : (
              <button
                className={classes.assignButton}
                onClick={() => assignPlanToMe()}
              >
                M'assigner
              </button>
            )}
          </IconChip>
          <IconChip icon="ri-briefcase-line" color="purple">
            PM :{' '}
            {pm ? (
              pmName
            ) : (
              <button
                className={classes.assignButton}
                onClick={() => assignSessionToMe()}
              >
                M'assigner
              </button>
            )}
          </IconChip>
        </div>
      </SessionBoxInfo>
      <Button
        text={t('sessions.details.monitoringAdmin.cleanButton')}
        color="red"
        variant="transparent"
        onClick={openWarning}
      />
      <CenteredModal
        isOpen={warnOpened}
        onRequestClose={closeWarning}
        title={t('sessions.details.monitoringAdmin.warnTitle')}
        text={t('sessions.details.monitoringAdmin.cleanWarnDescription')}
      >
        <div className={classes.modalInnerGap}>
          <Button
            text={t('sessions.details.monitoringAdmin.cancel')}
            onClick={closeWarning}
          />
          <Button
            color="red"
            text={t('sessions.details.monitoringAdmin.clean')}
            onClick={onSessionClean}
          />
        </div>
      </CenteredModal>
    </div>
  );
};
